/**
 * The pilot hazard pack (Diurnal parity 04-056, 05-030, 06-034; MB's decisions 88, 625, 808, 107, 83, 364;
 * docs/specs/map.md §33): one KML of a project's hazards for pilots, behind a link of the project that expires 30
 * days after the project ends and can be withdrawn (old addresses stop working). Oz Runways does not update an
 * overlay by itself (decision 808, replacing the network link of 625): pilots open the link, download the pack and
 * load it in Oz Runways (KML Overlays), and download it again after each new publish. The page says when the pack
 * was published and its version, so a pilot can tell whether their copy is current.
 *
 * Teslyn Field builds the KML (the core's `hazard_pack`) and sends it to Teslyn Cloud's `hazard-pack` Edge Function at
 * each sync with no button (decision 1042), which checks it here (`checkPackKml`) before keeping it as the project's
 * current pack. "Publish for Oz Runways" sends that pack out, and the function serves what was published and the
 * landing page's details by the link's token. This module has no imports: the Edge Function uses it through
 * `tools/dev/copy-edge-shared.mjs` (supabase/functions/_shared/domain/), so every exported type is declared here.
 */

/** Teslyn Cloud's Edge Function. */
export const HAZARD_PACK_FUNCTION = 'hazard-pack';
/** The largest pack Teslyn Cloud keeps. */
export const HAZARD_PACK_MAX_BYTES = 10 * 1024 * 1024;
/** A link works until this many days after the project ends (decision 625). */
export const HAZARD_PACK_DAYS_AFTER_END = 30;
/** The pack's name, at most. */
export const HAZARD_PACK_NAME_MAX = 120;
/** A link's token: 64 hex characters (two random UUIDs, made by the database). */
export const HAZARD_PACK_TOKEN = /^[0-9a-f]{64}$/;

// ------------------------------------------------------------------------------------------------ routes

/** What a request to the `hazard-pack` function asks for. */
export type HazardPackRoute =
  /** POST, signed in: Teslyn Field 2026.10.24 and earlier replace the pack and publish it for Oz Runways in one step. */
  | { readonly kind: 'publish' }
  /** POST `/sync`, signed in: Teslyn Field's automatic upload of the current pack (decision 1042). */
  | { readonly kind: 'sync' }
  /** GET `?project_id=`, signed in: the project's pack and link, for Teslyn Field's dialog. */
  | { readonly kind: 'status' }
  /** GET `/<token>.kml`: the pack's KML, "Download for Oz Runways". */
  | { readonly kind: 'kml'; readonly token: string }
  /** GET `/<token>/meta`: the landing page's details. */
  | { readonly kind: 'meta'; readonly token: string }
  | { readonly kind: 'unknown' };

/** The route of a request (the path after `hazard-pack`, with or without `/functions/v1`). */
export function hazardPackRoute(method: string, pathname: string): HazardPackRoute {
  const at = pathname.indexOf(`/${HAZARD_PACK_FUNCTION}`);
  if (at < 0) return { kind: 'unknown' };
  const rest = pathname.slice(at + HAZARD_PACK_FUNCTION.length + 1).replace(/\/+$/, '');
  const m = method.toUpperCase();
  if (rest === '') {
    if (m === 'POST') return { kind: 'publish' };
    if (m === 'GET') return { kind: 'status' };
    return { kind: 'unknown' };
  }
  if (rest === '/sync') return m === 'POST' ? { kind: 'sync' } : { kind: 'unknown' };
  if (m !== 'GET' && m !== 'HEAD') return { kind: 'unknown' };
  const r = /^\/([0-9a-f]{64})(\.kml|\/meta)$/.exec(rest);
  if (!r) return { kind: 'unknown' };
  const token = r[1]!;
  return r[2] === '.kml' ? { kind: 'kml', token } : { kind: 'meta', token };
}

/** The pack's addresses at Teslyn Cloud's functions. */
export interface HazardPackUrls {
  /** The pack's KML. */
  readonly kml: string;
  /** The landing page's details. */
  readonly meta: string;
}

export function hazardPackUrls(supabaseUrl: string, token: string): HazardPackUrls {
  const base = `${supabaseUrl.replace(/\/+$/, '')}/functions/v1/${HAZARD_PACK_FUNCTION}/${token}`;
  return { kml: `${base}.kml`, meta: `${base}/meta` };
}

/** The landing page the link opens: Teslyn Cloud's public page `/hazard-pack/<token>`. */
export function hazardPackLandingUrl(cloudBase: string, token: string): string {
  return `${cloudBase.replace(/\/+$/, '')}/hazard-pack/${token}`;
}

/** The token of a landing page address, or null. */
export function hazardPackTokenOfPath(pathname: string): string | null {
  const m = /^\/hazard-pack\/([0-9a-f]{64})\/?$/.exec(pathname);
  return m ? m[1]! : null;
}

/** The file a pilot's iPad gets. */
export interface HazardPackFiles {
  /** `F26048_Hazards.kml`: the tool's feed file name, so Oz Runways lists it the same. */
  readonly kml: string;
}

export function hazardPackFiles(projectNumber: string): HazardPackFiles {
  const safe = projectNumber.replace(/[^A-Za-z0-9_-]/g, '_') || 'Project';
  return { kml: `${safe}_Hazards.kml` };
}

// ------------------------------------------------------------------------------------------------ the KML

/**
 * The elements a pack may hold: the part of KML Oz Runways documents (Styles and StyleUrls, Placemarks, Points,
 * LineStrings, Polygons) in Folders. No network links, overlays, models, multi-geometries or ExtendedData.
 */
const PACK_ELEMENTS: ReadonlySet<string> = new Set([
  'kml',
  'Document',
  'Folder',
  'name',
  'open',
  'visibility',
  'description',
  'Placemark',
  'styleUrl',
  'Style',
  'IconStyle',
  'LabelStyle',
  'LineStyle',
  'PolyStyle',
  'color',
  'scale',
  'width',
  'fill',
  'outline',
  'Icon',
  'href',
  'Point',
  'LineString',
  'Polygon',
  'outerBoundaryIs',
  'innerBoundaryIs',
  'LinearRing',
  'coordinates',
]);

/** Markup in a description that a pilot's app could run. */
export function unsafeMarkup(s: string): boolean {
  return (
    /<\s*(script|iframe|object|embed|style|form)\b/i.test(s) ||
    /javascript\s*:/i.test(s) ||
    /<[^>]*\son[a-z]+\s*=/i.test(s)
  );
}

/** An icon a pack may carry: a Google Earth icon or an image inside the file. */
export function packIconAllowed(href: string): boolean {
  const h = href.trim();
  return (
    /^https?:\/\/maps\.google\.com\/mapfiles\/kml\/[A-Za-z0-9/_.-]+$/.test(h) ||
    /^data:image\/(png|jpeg|gif);base64,[A-Za-z0-9+/=]+$/.test(h)
  );
}

/** What `checkPackKml` found. */
export type PackKmlCheck =
  | {
      readonly ok: true;
      readonly placemarks: number;
      /** The folders directly in the document, in order. */
      readonly folders: readonly string[];
      readonly bytes: number;
    }
  | { readonly ok: false; readonly error: string };

const decode = (s: string): string =>
  s
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&amp;/g, '&');

const mb = (n: number): string => `${(n / 1024 / 1024).toFixed(1)} MB`;

/**
 * Teslyn Cloud's check of a pack before it is kept (inventory 04 §10.3: the server checks and limits what it is
 * sent): at most 10 MB, one `<kml>` holding one `<Document>`, only the elements Oz Runways reads, only Google Earth
 * icons or images inside the file, no markup that could run, and at least one placemark.
 */
export function checkPackKml(text: string): PackKmlCheck {
  const bytes = new TextEncoder().encode(text).length;
  if (bytes > HAZARD_PACK_MAX_BYTES)
    return {
      ok: false,
      error: `The hazard pack is ${mb(bytes)}; Teslyn Cloud takes at most ${mb(HAZARD_PACK_MAX_BYTES)}. Make the hazard map's search area smaller or remove a large map.`,
    };
  const bad = (why: string): PackKmlCheck => ({
    ok: false,
    error: `The hazard pack is not a KML Oz Runways can read: ${why}`,
  });
  const stack: string[] = [];
  let text_ = '';
  let placemarks = 0;
  let documents = 0;
  let rootSeen = false;
  const folders: string[] = [];
  let i = 0;
  const textCheck = (raw: string, cdata: boolean): string | null => {
    const top = stack[stack.length - 1];
    if (top === undefined) return raw.trim() === '' ? null : 'text outside the document';
    const value = cdata ? raw : decode(raw);
    if (top === 'description' && unsafeMarkup(value)) return 'a description holds markup that could run';
    if (top !== 'description' && top !== 'name' && unsafeMarkup(value)) return 'markup that could run';
    text_ += value;
    return null;
  };
  while (i < text.length) {
    const j = text.indexOf('<', i);
    const chunk = j < 0 ? text.slice(i) : text.slice(i, j);
    if (chunk) {
      const e = textCheck(chunk, false);
      if (e) return bad(e);
    }
    if (j < 0) break;
    if (text.startsWith('<![CDATA[', j)) {
      const k = text.indexOf(']]>', j + 9);
      if (k < 0) return bad('a block of description text is not closed');
      const e = textCheck(text.slice(j + 9, k), true);
      if (e) return bad(e);
      i = k + 3;
      continue;
    }
    if (text.startsWith('<!--', j)) {
      const k = text.indexOf('-->', j + 4);
      if (k < 0) return bad('a comment is not closed');
      i = k + 3;
      continue;
    }
    if (text.startsWith('<?', j)) {
      const k = text.indexOf('?>', j + 2);
      if (k < 0 || rootSeen) return bad('a processing instruction inside the document');
      i = k + 2;
      continue;
    }
    if (text.startsWith('<!', j)) return bad('a document type declaration');
    const k = text.indexOf('>', j);
    if (k < 0) return bad('a tag is not closed');
    const tag = text.slice(j + 1, k);
    i = k + 1;
    const closing = tag.startsWith('/');
    const selfClosing = !closing && tag.endsWith('/');
    const m = /^\/?([A-Za-z][A-Za-z0-9_.-]*)([\s\S]*?)\/?$/.exec(tag);
    if (!m) return bad(`the tag <${tag.slice(0, 40)}>`);
    const name = m[1]!;
    if (!PACK_ELEMENTS.has(name))
      return {
        ok: false,
        error: `The hazard pack holds <${name}>, which Oz Runways does not read. Teslyn Cloud takes points, lines and areas with their styles, in folders.`,
      };
    if (closing) {
      if (m[2]!.trim() !== '') return bad(`the closing tag </${name}>`);
      const open = stack.pop();
      if (open !== name) return bad(`<${open ?? 'nothing'}> is closed by </${name}>`);
      const parent = stack[stack.length - 1];
      if (name === 'href' && !packIconAllowed(text_))
        return bad('an icon from outside the file (only Google Earth icons and images inside the file)');
      if (name === 'name' && parent === 'Folder' && stack.length === 3) folders.push(text_.trim());
      text_ = '';
      continue;
    }
    // attributes: the KML namespace on <kml>, an id on <Style>
    const attrs = m[2]!;
    const okAttrs =
      name === 'kml'
        ? /^(\s+xmlns="http:\/\/www\.opengis\.net\/kml\/2\.2")?\s*$/.test(attrs)
        : name === 'Style'
          ? /^(\s+id="[A-Za-z0-9_.-]{1,80}")?\s*$/.test(attrs)
          : attrs.trim() === '';
    if (!okAttrs) return bad(`the attributes of <${name}>`);
    if (stack.length === 0) {
      if (name !== 'kml' || rootSeen) return bad('the file does not start with <kml>');
      rootSeen = true;
    } else if (name === 'kml') return bad('<kml> inside <kml>');
    if (name === 'Document') {
      if (stack.length !== 1) return bad('<Document> not directly in <kml>');
      documents += 1;
    }
    if (name === 'Placemark') placemarks += 1;
    text_ = '';
    if (!selfClosing) stack.push(name);
  }
  if (stack.length > 0) return bad(`<${stack[stack.length - 1]}> is not closed`);
  if (!rootSeen) return bad('the file does not start with <kml>');
  if (documents !== 1) return bad('it must hold one <Document>');
  if (placemarks === 0) return bad('it holds no placemark');
  return { ok: true, placemarks, folders, bytes };
}

// ------------------------------------------------------------------------------------------------ publish

/** What a pack holds (the core's `PackStats`). */
export interface HazardPackStats {
  readonly layers: readonly string[];
  readonly towers: number;
  readonly powerlines: number;
  readonly pins: number;
  readonly other_hazards: number;
  readonly properties: number;
  readonly survey_lines: number;
  readonly boundaries: number;
  readonly placemarks: number;
  readonly bytes: number;
}

const COUNTS = [
  'towers',
  'powerlines',
  'pins',
  'other_hazards',
  'properties',
  'survey_lines',
  'boundaries',
  'placemarks',
  'bytes',
] as const;

/** The stats as sent, or null when they are not the core's shape. */
export function parseHazardPackStats(v: unknown): HazardPackStats | null {
  if (!v || typeof v !== 'object') return null;
  const o = v as Record<string, unknown>;
  const layers = o['layers'];
  if (
    !Array.isArray(layers) ||
    layers.length > 200 ||
    !layers.every((l) => typeof l === 'string' && l.length <= 300)
  )
    return null;
  const out: Record<string, unknown> = { layers: [...(layers as string[])] };
  for (const k of COUNTS) {
    const n = o[k];
    if (typeof n !== 'number' || !Number.isInteger(n) || n < 0 || n > 100_000_000) return null;
    out[k] = n;
  }
  return out as unknown as HazardPackStats;
}

/** A publish request from Teslyn Field. */
export interface HazardPackPublish {
  readonly project_id: string;
  readonly name: string;
  readonly kml: string;
  readonly stats: HazardPackStats;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function parseHazardPackPublish(
  body: unknown,
): { readonly ok: true; readonly value: HazardPackPublish } | { readonly ok: false; readonly error: string } {
  if (!body || typeof body !== 'object') return { ok: false, error: 'The request must be a JSON object.' };
  const o = body as Record<string, unknown>;
  const projectId = o['project_id'];
  if (typeof projectId !== 'string' || !UUID.test(projectId))
    return { ok: false, error: 'Say which project the hazard pack is for.' };
  const name = typeof o['name'] === 'string' ? o['name'].trim() : '';
  if (name.length < 1 || name.length > HAZARD_PACK_NAME_MAX)
    return {
      ok: false,
      error: `Give the hazard pack a name of 1 to ${HAZARD_PACK_NAME_MAX} characters.`,
    };
  const kml = o['kml'];
  if (typeof kml !== 'string' || kml.length === 0) return { ok: false, error: 'The hazard pack has no KML.' };
  const stats = parseHazardPackStats(o['stats']);
  if (!stats) return { ok: false, error: 'The hazard pack does not say what it holds.' };
  return { ok: true, value: { project_id: projectId.toLowerCase(), name, kml, stats } };
}

/** Teslyn Field's automatic upload (decision 1042): the pack, when its maps changed, and which maps it holds. */
export interface HazardPackSync extends HazardPackPublish {
  /** When the hazard maps of this pack last changed on the laptop: the latest change wins. */
  readonly changed_at: string;
  /** The hazard maps it was built from, by name. */
  readonly maps: readonly string[];
}

/** At most this many hazard maps are named with a pack. */
export const HAZARD_PACK_MAPS_MAX = 200;

export function parseHazardPackSync(
  body: unknown,
): { readonly ok: true; readonly value: HazardPackSync } | { readonly ok: false; readonly error: string } {
  const base = parseHazardPackPublish(body);
  if (!base.ok) return base;
  const o = body as Record<string, unknown>;
  const changedAt = o['changed_at'];
  if (typeof changedAt !== 'string' || Number.isNaN(Date.parse(changedAt)))
    return { ok: false, error: 'The hazard pack does not say when its maps changed.' };
  const maps = o['maps'];
  if (
    !Array.isArray(maps) ||
    maps.length > HAZARD_PACK_MAPS_MAX ||
    !maps.every((m) => typeof m === 'string' && m.length <= 500)
  )
    return { ok: false, error: 'The hazard pack does not say which hazard maps it holds.' };
  return { ok: true, value: { ...base.value, changed_at: changedAt, maps: maps as string[] } };
}

/** What Teslyn Cloud did with an automatic upload. */
export type HazardPackSyncState = 'synced' | 'unchanged' | 'newer_in_cloud';

// ------------------------------------------------------------------------------------------------ links

/** A link works, was withdrawn, or expired 30 days after its project ended. */
export type HazardPackLinkState = 'live' | 'revoked' | 'expired';

/** A link as Teslyn Cloud keeps it. */
export interface HazardPackLinkTimes {
  /** Set when the project ends (status Complete or Archived): the end plus 30 days; null while it runs. */
  readonly expires_at: string | null;
  readonly revoked_at: string | null;
}

export function hazardPackLinkState(link: HazardPackLinkTimes, nowMs: number): HazardPackLinkState {
  if (link.revoked_at) return 'revoked';
  if (link.expires_at && Date.parse(link.expires_at) <= nowMs) return 'expired';
  return 'live';
}

/** "27 Oct 2026" in the given time zone (the viewer's when none). */
export function hazardPackDate(iso: string, timeZone?: string): string {
  return new Intl.DateTimeFormat('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    ...(timeZone ? { timeZone } : {}),
  }).format(new Date(iso));
}

/** "27 Oct 2026, 2:05 pm" in the given time zone. */
export function hazardPackDateTime(iso: string, timeZone?: string): string {
  return new Intl.DateTimeFormat('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    ...(timeZone ? { timeZone } : {}),
  })
    .format(new Date(iso))
    .replace(/\s?([ap])m$/i, (_m, a: string) => ` ${a.toLowerCase()}m`);
}

/** How long a link works, in words. */
export function hazardPackExpiryWords(link: HazardPackLinkTimes, nowMs: number, timeZone?: string): string {
  const state = hazardPackLinkState(link, nowMs);
  if (state === 'revoked')
    return `Withdrawn on ${hazardPackDate(link.revoked_at!, timeZone)}: the link no longer works.`;
  if (state === 'expired')
    return `Expired on ${hazardPackDate(link.expires_at!, timeZone)}, 30 days after the project ended: the link no longer works.`;
  if (link.expires_at)
    return `Works until ${hazardPackDate(link.expires_at, timeZone)} (30 days after the project ended).`;
  return 'Works until 30 days after the project ends.';
}

/** The landing page's details (`/<token>/meta`). */
export interface HazardPackMeta {
  readonly state: HazardPackLinkState;
  readonly organisation: string;
  readonly project_number: string;
  readonly name: string;
  readonly published_at: string;
  readonly version: number;
  readonly stats: HazardPackStats;
  readonly expires_at: string | null;
  readonly revoked_at: string | null;
  /** When a hazard's mark last changed, Present included (decision 1053); null for none, absent before it. */
  readonly marks_changed_at?: string | null;
  readonly files: HazardPackFiles;
  readonly urls: HazardPackUrls;
}

const plural = (n: number, one: string, many: string): string => `${n} ${n === 1 ? one : many}`;

/** What a pack holds, in words, for the landing page, Teslyn Field and Teslyn Cloud. */
export function hazardPackContents(stats: HazardPackStats): string[] {
  const out: string[] = [];
  if (stats.towers > 0) out.push(plural(stats.towers, 'communication tower', 'communication towers'));
  if (stats.powerlines > 0) out.push(plural(stats.powerlines, 'powerline', 'powerlines'));
  if (stats.pins > 0) out.push(plural(stats.pins, 'hazard pin', 'hazard pins'));
  if (stats.other_hazards > 0) out.push(plural(stats.other_hazards, 'other hazard', 'other hazards'));
  if (stats.boundaries > 0) out.push(plural(stats.boundaries, 'survey boundary', 'survey boundaries'));
  if (stats.properties > 0)
    out.push(plural(stats.properties, 'landholder property', 'landholder properties'));
  if (stats.survey_lines > 0) out.push(plural(stats.survey_lines, 'survey line', 'survey lines'));
  return out;
}

/** How a pilot loads the pack in Oz Runways (the tool's landing page steps, decision 808). */
export function hazardPackSteps(files: HazardPackFiles): string[] {
  const overlay = files.kml.replace(/\.kml$/, '');
  return [
    'Open this page on the iPad, in Safari.',
    'Tap "Download for Oz Runways".',
    `Tap the download icon in Safari's toolbar, then tap ${files.kml}.`,
    'Tap the Share icon and choose "Copy to Oz RWY" (scroll the row of apps to find it).',
    `In Oz Runways open Menu, KML Overlays, and switch ${overlay} on.`,
  ];
}

/** Oz Runways does not update the pack by itself (decision 808): what a pilot does after a new Publish for Oz Runways. */
export function hazardPackUpdateWords(files: HazardPackFiles): string {
  const overlay = files.kml.replace(/\.kml$/, '');
  return `Oz Runways does not update the pack by itself. When the office publishes it again, or a hazard is marked not present, download it again: first remove the old ${overlay} from KML Overlays, so the hazards are not shown twice.`;
}

/** When a hazard's mark last changed (decision 1053): the copy in a pilot's Oz Runways is as old as its download. */
export function hazardPackMarksWords(changedAt: string, timeZone?: string): string {
  return `Hazards marked not present last changed ${hazardPackDateTime(changedAt, timeZone)}`;
}

/** "Published 27 Oct 2026, 2:05 pm, version 3": so a pilot can tell whether their copy is current. */
export function hazardPackVersionWords(publishedAt: string, version: number, timeZone?: string): string {
  return `Published ${hazardPackDateTime(publishedAt, timeZone)}, version ${version}`;
}

/** The email "Email to pilots" opens (the tool's `showPublishSuccessDialog` text), for the person to change. */
export function hazardPackEmail(p: {
  readonly projectNumber: string;
  readonly name: string;
  readonly landing: string;
  readonly organisation: string;
  readonly expiry: string;
}): { readonly subject: string; readonly body: string } {
  const files = hazardPackFiles(p.projectNumber);
  return {
    subject: `${p.projectNumber} hazard pack for Oz Runways`,
    body: [
      'Hi,',
      '',
      `The hazard pack for project ${p.projectNumber} (${p.name}) is ready for Oz Runways.`,
      '',
      'Open this link on your iPad:',
      p.landing,
      '',
      'How to load it:',
      ...hazardPackSteps(files)
        .slice(1)
        .map((s, i) => `${i + 1}. ${s}`),
      '',
      'Oz Runways does not update it by itself: download it again when we send a new link or tell you it changed.',
      p.expiry,
      '',
      'Regards,',
      p.organisation,
    ].join('\n'),
  };
}

/** A `mailto:` address with the email filled in. */
export function hazardPackMailto(email: { readonly subject: string; readonly body: string }): string {
  return `mailto:?subject=${encodeURIComponent(email.subject)}&body=${encodeURIComponent(email.body)}`;
}
